import { useCallback } from "react";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import {
  GenerateUrlRequestBody,
  Paginated,
  PaginationQuery,
  UpdateOrderRequestBody,
  UpdateVisibilityRequestBody,
} from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import {
  AdminInstallationType,
  UpdateInstallationTypeNewImageBody,
  UpdateInstallationTypeRemoveImageBody,
} from "./types/installationTypes";

export const useInstallationTypesApi = () => {
  const api = useApi();

  const getAdminInstallationType = useCallback(
    (signal: AbortSignal) => {
      const url = `/admin/settings/vertigrip/installation_types`;
      return useSWRMutation<AdminInstallationType, CustomError, string, string>(url, (url, { arg }: { arg: string }) =>
        api.get<AdminInstallationType>({ url: `${url}/${arg}`, signal }),
      );
    },
    [api],
  );

  const useAdminInstallationTypes = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl("/admin/settings/vertigrip/installation_types", query);
      return useSWR<Paginated<AdminInstallationType>, CustomError, string>(url, (url) =>
        api.get<Paginated<AdminInstallationType>>({ url, signal }),
      );
    },
    [api],
  );

  const updateAdminInstallationType = useCallback(
    async (
      installationTypeId: string,
      body: UpdateInstallationTypeRemoveImageBody | UpdateInstallationTypeNewImageBody,
    ): Promise<void> => {
      const url = `/admin/settings/vertigrip/installation_types/${installationTypeId}`;
      (await api.put<UpdateInstallationTypeRemoveImageBody | UpdateInstallationTypeNewImageBody>(url, body),
        mutateCache(["/admin/settings/vertigrip/installation_types"]));
    },
    [api],
  );

  const deleteAdminInstallationType = useCallback(
    async (installationTypeId: string): Promise<void> => {
      const url = `/admin/settings/vertigrip/installation_types/${installationTypeId}`;
      await api.del(url);
      mutateCache([`/admin/settings/vertigrip/installation_types`]);
    },
    [api],
  );

  const generateUploadImageUrl = useCallback(
    async (installationTypeId: string, fileName: string, fileType: string): Promise<string> => {
      const encodedFileName = encodeURIComponent(fileName);
      const url = `/admin/settings/vertigrip/installation_types/${installationTypeId}/image/generate_upload_url?file_name=${encodedFileName}`;
      return api.post<string, GenerateUrlRequestBody>(url, {
        filename: fileName,
        contentType: fileType,
      });
    },
    [api],
  );

  const updateAdminInstallationTypeVisibility = useCallback(
    async (installationTypeId: string, visibility: UpdateVisibilityRequestBody): Promise<void> => {
      const url = `/admin/settings/vertigrip/installation_types/${installationTypeId}/visibility`;
      await api.put<UpdateVisibilityRequestBody>(url, visibility);
      mutateCache(["/admin/settings/vertigrip/installation_types"]);
    },
    [api],
  );

  const updateAdminInstallationTypeOrder = useCallback(
    async (installationTypeId: string, order: UpdateOrderRequestBody): Promise<void> => {
      const url = `/admin/settings/vertigrip/installation_types/${installationTypeId}/order`;
      await api.put<UpdateOrderRequestBody>(url, order);
      mutateCache(["/admin/settings/vertigrip/installation_types"]);
    },
    [api],
  );

  return {
    getAdminInstallationType,
    useAdminInstallationTypes,
    updateAdminInstallationType,
    updateAdminInstallationTypeVisibility,
    updateAdminInstallationTypeOrder,
    deleteAdminInstallationType,
    generateUploadImageUrl,
  };
};
